import { useEffect } from "react";
import {
  child,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  onDisconnect,
  ref,
  remove,
} from "firebase/database";
import { rtdb } from "initializer";
import { useAppDispatch } from "stores";
import {
  roomMemberAdded,
  roomMemberRemoved,
  roomMemberUpdate,
  roomMemberUpdated,
} from "./roomSlice";

const membersRef = (roomId: string) => ref(rtdb, `room-members/${roomId}`);

type UseRoomMemberProps = {
  roomId?: string | null;
  uid?: string | null;
};

const useRoomMember = ({ roomId, uid }: UseRoomMemberProps) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!roomId || !uid) return;

    dispatch(roomMemberUpdate("none"));
    const disconnect = onDisconnect(child(membersRef(roomId), uid));
    disconnect.remove();

    const unsubscribeOnChildAdded = onChildAdded(membersRef(roomId), (data) => {
      dispatch(
        roomMemberAdded({
          uid: data.key,
          ...data.val(),
        })
      );
    });
    const unsubscribeOnChildChanged = onChildChanged(
      membersRef(roomId),
      (data) => {
        dispatch(
          roomMemberUpdated({
            uid: data.key,
            ...data.val(),
          })
        );
      }
    );
    const unsubscribeOnChildRemoved = onChildRemoved(
      membersRef(roomId),
      (data) => {
        data.key && dispatch(roomMemberRemoved(data.key));
      }
    );

    return () => {
      disconnect.cancel();
      unsubscribeOnChildAdded();
      unsubscribeOnChildChanged();
      unsubscribeOnChildRemoved();
      remove(child(membersRef(roomId), uid));
    };
  }, [dispatch, roomId, uid]);
};

export default useRoomMember;
